import { createContext, useCallback, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { projects, getActiveProjectId, setActiveProjectId, type ProjectInfo } from '../lib/api';
import { can, type RoleAction } from '../lib/roles';
import { useResource } from './useResource';

/**
 * Active-project state for the authenticated shell.
 *
 * The list of projects (with the user's role in each) comes from /projects;
 * the active one is whichever localStorage points at, validated against the
 * list (a stale selection silently falls back to the first project rather
 * than wedging the UI). Switching writes localStorage — every API call sends
 * it as X-Project-Id — and the layout remounts the screen tree so all
 * resources refetch under the new scope.
 */
interface ProjectState {
  projects: ProjectInfo[];
  project: ProjectInfo | null;
  loading: boolean;
  switchProject: (projectId: string) => void;
  reloadProjects: () => Promise<void>;
  /** Role-matrix check for the active project. */
  allowed: (action: RoleAction) => boolean;
}

const ProjectContext = createContext<ProjectState | null>(null);

export function ProjectProvider({ children }: { children: ReactNode }) {
  const list = useResource(() => projects.list(), []);

  const all = useMemo(() => list.data ?? [], [list.data]);

  const project = useMemo(() => {
    const activeId = getActiveProjectId();
    return all.find((candidate) => candidate.id === activeId) ?? all[0] ?? null;
  }, [all]);

  // Keep storage honest when the stored id no longer resolves.
  if (project && getActiveProjectId() !== project.id) setActiveProjectId(project.id);

  const switchProject = useCallback(
    (projectId: string) => {
      if (projectId === project?.id) return;
      setActiveProjectId(projectId);
      // Full reload: cheapest correct way to drop every project-scoped
      // resource at once (screens, polls, modals mid-flight).
      window.location.reload();
    },
    [project?.id]
  );

  const allowed = useCallback(
    (action: RoleAction) => can(project?.role, action),
    [project?.role]
  );

  const value = useMemo(
    () => ({
      projects: all,
      project,
      loading: list.loading,
      switchProject,
      reloadProjects: () => list.reload(),
      allowed,
    }),
    [all, project, list.loading, switchProject, allowed, list]
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject(): ProjectState {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used inside a ProjectProvider');
  }
  return context;
}
